import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, History, Save } from "lucide-react";
import { API_V1_BASE_URL } from "../config/api";

interface TncItem {
  id: string;
  tenant_id: string;
  version: string;
  content: string;
  created_at: string;
  created_by: string | null;
  is_active: boolean;
}

interface TenantTncResponse {
  tenant: { id: string; name: string; tncVersion: string | null };
  current: TncItem | null;
  history: TncItem[];
}

const TenantTncManagement: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();

  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [tenant, setTenant] = useState<TenantTncResponse["tenant"] | null>(null);
  const [currentTnc, setCurrentTnc] = useState<TncItem | null>(null);
  const [history, setHistory] = useState<TncItem[]>([]);
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const accessToken = localStorage.getItem("accessToken");

  const fetchTenantTnc = async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${API_V1_BASE_URL}/admin/tenants/${tenantId}/tnc`, {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      const result = await res.json();
      if (!res.ok || !result?.success) throw new Error(result?.message || "Unable to load tenant T&C.");
      const data: TenantTncResponse = result.data;
      setTenant(data.tenant);
      setCurrentTnc(data.current);
      setHistory(data.history || []);
      setContent("");
    } catch (err: any) {
      setError(err?.message || "Unable to load tenant Terms and Conditions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenantTnc(); }, [tenantId]);

  const handlePublish = async () => {
    setError(""); setSuccess("");
    if (!content.trim()) { setError("Please enter the Terms and Conditions."); return; }
    try {
      setPublishing(true);
      const res = await fetch(`${API_V1_BASE_URL}/admin/tenants/${tenantId}/tnc`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ content: content.trim() }),
      });
      const result = await res.json();
      if (!res.ok || !result?.success) throw new Error(result?.message || "Unable to publish Terms and Conditions.");
      setSuccess(`T&C version ${result.data?.tnc?.version} published successfully.`);
      setContent("");
      await fetchTenantTnc();
    } catch (err: any) {
      setError(err?.message || "Unable to publish Terms and Conditions.");
    } finally { setPublishing(false); }
  };

  const formatDate = (date: string) => date ? new Date(date).toLocaleString() : "-";

  const nextVersion = currentTnc
    ? (() => {
        const v = currentTnc.version.replace(/^v/i, "").split(".");
        let major = Number(v[0]), minor = Number(v[1]);
        if (minor >= 10) { major += 1; minor = 0; } else { minor += 1; }
        return `${major}.${minor}`;
      })()
    : "1.0";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 shadow-sm">
          Loading Tenant T&C...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <button
            onClick={() => navigate("/super-admin/tenant-users?view=tenants")}
            className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 transition-colors mb-3"
          >
            <ArrowLeft size={15} /> Back to Tenant Management
          </button>
          <h1 className="text-2xl font-bold text-slate-900">Manage Tenant T&C</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage Terms and Conditions for{" "}
            <span className="font-medium text-slate-700">{tenant?.name || "this tenant"}</span>
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 shadow-sm">
            {success}
          </div>
        )}

        {/* Current T&C */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <FileText size={18} className="text-emerald-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Current Terms and Conditions</h2>
                <p className="text-xs text-slate-500 mt-0.5">Currently published version for this tenant</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            {currentTnc ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Version {currentTnc.version}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                    Active
                  </span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 max-h-[400px] overflow-y-auto">
                  <p className="text-sm leading-6 text-slate-700 whitespace-pre-wrap">{currentTnc.content}</p>
                </div>
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                No Terms and Conditions have been published for this tenant yet. The first version will automatically be <strong>1.0</strong>.
              </div>
            )}
          </div>
        </div>

        {/* Publish New T&C */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                <FileText size={18} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Publish New Version</h2>
                <p className="text-xs text-slate-500 mt-0.5">Publishing a new T&C automatically creates the next version.</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3">
              <p className="text-xs text-indigo-600 font-medium">Next version</p>
              <p className="text-lg font-bold text-indigo-800 mt-1">{nextVersion}</p>
              <p className="text-xs text-indigo-600 mt-1">Version is generated automatically. You do not need to enter it.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Terms and Conditions</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={9}
                placeholder="Enter the complete Terms and Conditions..."
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 resize-y transition-shadow"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handlePublish}
                disabled={publishing || !content.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors shadow-sm"
              >
                <Save size={16} />
                {publishing ? "Publishing..." : "Publish T&C"}
              </button>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <History size={18} className="text-slate-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">T&C Version History</h2>
                <p className="text-xs text-slate-500 mt-0.5">Previous versions published for this tenant</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            {history.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No T&C history available.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">VERSION</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">STATUS</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">PUBLISHED</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">CONTENT</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-800">{item.version}</td>
                      <td className="px-4 py-3">
                        {item.is_active ? (
                          <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Active</span>
                        ) : (
                          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">Previous</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDate(item.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-xl">
                        <div className="line-clamp-3 whitespace-pre-wrap">{item.content}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantTncManagement;